import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Pagination,
  Menu,
  MenuItem,
  styled,
  useTheme,
  useMediaQuery,
  Tooltip,
  TextField,
  InputAdornment,
  AppBar,
  Toolbar,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import SearchIcon from "@mui/icons-material/Search";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import EditGrobalPosition from "./EditGrobalPosition";
import LeftToolbar from "./LeftToolbar";
import Footer from "./Footer";
(() => {
  if (!document.querySelector("[data-rc-font]")) {
    const l = document.createElement("link");
    l.rel = "stylesheet";
    l.href =
      "https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800&family=Playfair+Display:wght@400;600&display=swap";
    l.setAttribute("data-rc-font", "");
    document.head.appendChild(l);
  }
})();
const MainContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  minHeight: "100vh",
  backgroundColor: "#f5f7fa",
  fontFamily: "'Plus Jakarta Sans', sans-serif",
});

const MainContent = styled(Box)(({ theme, $collapsed }) => ({
  flexGrow: 1,
  padding: theme.spacing(3),
  marginLeft: $collapsed ? "80px" : "280px",
  transition: theme.transitions.create("margin", {
    easing: theme.transitions.easing.easeInOut,
    duration: theme.transitions.duration.standard,
  }),
  [theme.breakpoints.down("sm")]: {
    marginLeft: 0,
    padding: theme.spacing(2),
  },
}));

const ResponsiveTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: "16px",
  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.05)",
  overflowX: "auto",
  background: "white",
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  "&::-webkit-scrollbar": {
    height: "6px",
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: alpha(theme.palette.primary.main, 0.3),
    borderRadius: "3px",
  },
  [theme.breakpoints.down("sm")]: {
    borderRadius: "12px",
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(even)": {
    backgroundColor: alpha(theme.palette.primary.light, 0.03),
  },
  "&:hover": {
    backgroundColor: alpha(theme.palette.primary.light, 0.08),
    transform: "translateY(-2px)",
    boxShadow: theme.shadows[1],
  },
  transition: theme.transitions.create(["background-color", "transform"], {
    duration: theme.transitions.duration.shortest,
  }),
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

const ModernTableCell = styled(TableCell)(({ theme }) => ({
  padding: theme.spacing(2),
  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  fontFamily: "'Plus Jakarta Sans', sans-serif",
  "&:first-of-type": {
    borderLeft: `4px solid ${theme.palette.primary.main}`,
  },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(1.5),
  },
}));
const StatusBadge = styled("span")(({ active, theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  padding: "4px 12px",
  borderRadius: "16px",
  fontWeight: 600,
  fontSize: "0.75rem",
  textTransform: "capitalize",
  fontFamily: "'Plus Jakarta Sans', sans-serif",
  backgroundColor: active
    ? alpha(theme.palette.success.light, 0.2)
    : alpha(theme.palette.error.light, 0.2),
  color: active ? theme.palette.success.dark : theme.palette.error.dark,
  "&:before": {
    content: '""',
    width: 6,
    height: 6,
    borderRadius: "50%",
    marginRight: 6,
    backgroundColor: active
      ? theme.palette.success.main
      : theme.palette.error.main,
  },
}));

function MappingGrobalPositions() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [collapsed, setCollapsed] = useState(false);
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({
    key: "position",
    direction: "asc",
  });
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [openEditGrobalPosition, setOpenEditGrobalPosition] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const itemsPerPage = 8;
  useEffect(() => {
    fetchPositions();
  }, []);
  const fetchPositions = async () => {
    try {
      setLoading(true);
      const res = await fetch("/api/support/grobal-positions");
      const data = await res.json();
      setPositions(data.recordset || data);
    } catch (err) {
      console.error("Error fetching positions:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (key) => {
    if (key === "actions") return;
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const filteredPositions = positions.filter((item) => {
    const s = searchTerm.toLowerCase();
    return (
      item.position?.toLowerCase().includes(s) ||
      String(item.id)?.toLowerCase().includes(s)
    );
  });
  const sortedPositions = [...filteredPositions].sort((a, b) => {
    const nameCompare = a.position.localeCompare(b.position);
    if (sortConfig.key === "position") {
      return sortConfig.direction === "asc" ? nameCompare : -nameCompare;
    }
    const aVal = a[sortConfig.key];
    const bVal = b[sortConfig.key];
    if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
    if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;

    return 0;
  });
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = sortedPositions.slice(indexOfFirstItem, indexOfLastItem);
  const handleMenuOpen = (e, item) => {
    setAnchorEl(e.currentTarget);
    setSelectedPosition(item);
  };
  const handleMenuClose = () => setAnchorEl(null);

  const highlightMatch = (text, term) => {
    if (!term || !text) return text;
    const escapedTerm = term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const regex = new RegExp(`(${escapedTerm})`, "gi");
    return text
      .toString()
      .split(regex)
      .map((part, i) =>
        regex.test(part) ? (
          <mark key={i} style={{ backgroundColor: "#ffeb3b", padding: 0 }}>
            {part}
          </mark>
        ) : (
          part
        ),
      );
  };
const safeValue = (value) => {
  return value ?? "--";
};
  return (
    <MainContainer>
      <AppBar
        position="fixed"
        sx={{
          backgroundImage: "linear-gradient(195deg, #0a2e52 0%, #1565C0 200%)",
          boxShadow:
            "0 4px 20px 0 rgba(0, 0, 0, 0.14), 0 7px 10px -5px rgba(0, 0, 0, 0.4)",
          zIndex: (theme) => theme.zIndex.drawer + 1,
          left: collapsed ? "80px" : "280px",
          width: `calc(100% - ${collapsed ? "80px" : "280px"})`,
          transition: theme.transitions.create(["width", "left"], {
            easing: theme.transitions.easing.easeInOut,
            duration: theme.transitions.duration.standard,
          }),
          [theme.breakpoints.down("sm")]: {
            left: 0,
            width: "100%",
          },
        }}
      >
        <Toolbar sx={{ justifyContent: "space-between" }}>
          <Typography
            variant="h6"
            noWrap
            sx={{
              fontWeight: 600,
              fontFamily: "'Plus Jakarta Sans', sans-serif",
            }}
          >
            IPPIS SUPPORT PORTAL
          </Typography>
        </Toolbar>
      </AppBar>

      <LeftToolbar
        selectedMenu="Mapping Grobal Positions"
        onSelectMenu={() => { }}
        onCollapse={setCollapsed}
      />

      <MainContent $collapsed={collapsed}>
        <Toolbar /> 
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 4,
            gap: 2,
            flexDirection: isMobile ? "column" : "row",
          }}
        >
          <TextField
            label="Search positions..."
            variant="outlined"
            size="small"
            fullWidth
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
            sx={{
              width: isMobile ? "100%" : 400,
              "& .MuiOutlinedInput-root": {
                borderRadius: "12px",
                backgroundColor: "white",
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
        </Box>
        {/* Tickets table area */}
        <Box
          sx={{
            backgroundColor: "white",
            borderRadius: "16px",
            p: 3,
            mb: 3,
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.05)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                fontFamily: "'Plus Jakarta Sans', sans-serif",
              }}
            >
              Global Positions Mapping
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
            >
              {filteredPositions.length}{" "}
              {filteredPositions.length === 1 ? "position" : "positions"} found
            </Typography>
          </Box>

          <ResponsiveTableContainer component={Paper}>
            <Table size={isMobile ? "small" : "medium"}>
              <TableHead>
                <TableRow
                  sx={{
                    bgcolor: theme.palette.primary.light,
                    "& th": {
                      color: "white",
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                      position: "sticky",
                      top: 0,
                      zIndex: 1,
                      fontFamily: "'Plus Jakarta Sans', sans-serif",
                    },
                  }}
                >
                  {[
                    { id: "id", label: "No" },
                    { id: "position", label: "Position" },

                    { id: "majorGroup", label: "Major Group" },
                    { id: "subMajorGroup", label: "Sub Major Group" },
                    { id: "minorGroup", label: "Minor Group" },
                    {
                      id: "ISCO_OccupationUnit",
                      label: "ISCO Occupation Unit",
                    },
                    { id: "isActive", label: "Status" },
                    { id: "actions", label: "Actions" },
                  ].map((column) => (
                    <ModernTableCell
                      key={column.id}
                      onClick={() => handleSort(column.id)}
                      sx={{
                        cursor: column.id !== "actions" ? "pointer" : "default",
                        "&:hover": {
                          backgroundColor:
                            column.id !== "actions"
                              ? alpha(theme.palette.primary.main, 0.2)
                              : "inherit",
                        },
                      }}
                    >
                      <Box display="flex" alignItems="center">
                        {column.label}
                        {column.id !== "actions" && (
                          <Box ml={1} display="flex" flexDirection="column">
                            <ArrowUpwardIcon
                              sx={{
                                fontSize: "0.7rem",
                                color:
                                  sortConfig.key === column.id &&
                                    sortConfig.direction === "asc"
                                    ? "#fff"
                                    : alpha("#fff", 0.5),
                                mb: "-2px",
                              }}
                            />
                            <ArrowDownwardIcon
                              sx={{
                                fontSize: "0.7rem",
                                color:
                                  sortConfig.key === column.id &&
                                    sortConfig.direction === "desc"
                                    ? "#fff"
                                    : alpha("#fff", 0.5),
                              }}
                            />
                          </Box>
                        )}
                      </Box>
                    </ModernTableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <ModernTableCell colSpan={12} align="center" sx={{ py: 4 }}>
                      <CircularProgress size={24} />
                      <Typography
                        variant="body2"
                        sx={{
                          mt: 1,
                          fontFamily: "'Plus Jakarta Sans', sans-serif",
                        }}
                      >
                        Loading positions...
                      </Typography>
                    </ModernTableCell>
                  </TableRow>
                ) : currentItems.length > 0 ? (
                  currentItems.map((item, index) => (
                    <StyledTableRow key={item.id}>
                      <ModernTableCell>
                        <Typography
                          sx={{
                            fontWeight: 600,
                            fontFamily: "'Plus Jakarta Sans', sans-serif",
                          }}
                        >
                          {String(indexOfFirstItem + index + 1).padStart(
                            2,
                            "0",
                          )}
                        </Typography>
                      </ModernTableCell>
                      <ModernTableCell>
                        <Typography
                          sx={{
                            fontSize: "0.95rem",
                            color: "text.primary",
                            fontFamily: "'Plus Jakarta Sans', sans-serif",
                          }}
                        >
                          {highlightMatch(item.position, searchTerm)}
                        </Typography>
                      </ModernTableCell>
                      <ModernTableCell>
                        <Typography
                          sx={{
                            fontSize: "0.95rem",
                            color: "text.primary",
                            fontFamily: "'Plus Jakarta Sans', sans-serif",
                          }}
                        >
                          {highlightMatch(safeValue(item.majorGroupName, searchTerm))}
                        </Typography>
                      </ModernTableCell>
                      <ModernTableCell>
                        <Typography
                          sx={{
                            fontSize: "0.95rem",
                            color: "text.primary",
                            fontFamily: "'Plus Jakarta Sans', sans-serif",
                          }}
                        >
                          {highlightMatch(safeValue(item.subMajorGroupName, searchTerm))}
                        </Typography>
                      </ModernTableCell>

                      <ModernTableCell>
                        <Typography
                          sx={{
                            fontSize: "0.95rem",
                            color: "text.primary",
                            fontFamily: "'Plus Jakarta Sans', sans-serif",
                          }}
                        >
                          {highlightMatch(safeValue(item.minorGroupName, searchTerm))}
                        </Typography>
                      </ModernTableCell>

                      <ModernTableCell>
                        <Typography
                          sx={{
                            fontSize: "0.95rem",
                            color: "text.primary",
                            fontFamily: "'Plus Jakarta Sans', sans-serif",
                          }}
                        >
                          {highlightMatch(safeValue(item.ISCO_OccupationUnit, searchTerm))}
                        </Typography>
                      </ModernTableCell>
                      <ModernTableCell>
                        <StatusBadge active={item.isActive}>
                          {item.isActive ? "Active" : "Inactive"}
                        </StatusBadge>
                      </ModernTableCell>

                      <ModernTableCell>
                        <Tooltip title="More options" arrow placement="left">
                          <IconButton
                            color="primary"
                            onClick={(e) => handleMenuOpen(e, item)}
                            size={isMobile ? "small" : "medium"}
                            sx={{
                              "&:hover": {
                                backgroundColor: alpha(
                                  theme.palette.primary.main,
                                  0.1,
                                ),
                              },
                            }}
                          >
                            <MoreVertIcon
                              fontSize={isMobile ? "small" : "medium"}
                            />
                          </IconButton>
                        </Tooltip>
                      </ModernTableCell>
                    </StyledTableRow>
                  ))
                ) : (
                  <TableRow>
                    <ModernTableCell colSpan={4} align="center" sx={{ py: 4 }}>
                      <Typography
                        variant="body1"
                        sx={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                      >
                        No positions found
                      </Typography>
                    </ModernTableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </ResponsiveTableContainer>

          {/* Pagination */}
          {filteredPositions.length > 0 && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
              <Pagination
                count={Math.ceil(filteredPositions.length / itemsPerPage)}
                page={currentPage}
                onChange={(e, value) => setCurrentPage(value)}
                color="primary"
                shape="rounded"
                size={isMobile ? "small" : "medium"}
                sx={{
                  "& .MuiPaginationItem-root": { borderRadius: "999px" },
                }}
              />
            </Box>
          )}
        </Box>
      </MainContent>

      <Footer collapsed={collapsed} />

      {/* Context menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            mt: 1,
            boxShadow: "0 8px 32px rgba(0,0,0,0.08)",
            borderRadius: "12px",
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            minWidth: "160px",
            "& .MuiMenuItem-root": {
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontSize: "0.875rem",
              padding: "10px 16px",
              gap: "12px",
              borderRadius: "8px",
              margin: "4px 8px",
              "&:hover": {
                backgroundColor: alpha(theme.palette.primary.main, 0.08),
                color: theme.palette.primary.main,
              },
            },
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <MenuItem
          onClick={() => {
            setOpenEditGrobalPosition(true);
            handleMenuClose();
          }}
        >
          <EditIcon
            fontSize="small"
            sx={{ color: theme.palette.primary.main }}
          />
          Edit Position
        </MenuItem>
      </Menu>

      {/* Edit modal */}
      <EditGrobalPosition
        open={openEditGrobalPosition}
        onClose={() => setOpenEditGrobalPosition(false)}
        onSaveSuccess={fetchPositions}
        position={selectedPosition}
      />
    </MainContainer>
  );
}

export default MappingGrobalPositions;
